import { TrashIcon } from '@heroicons/react/24/solid';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import type { FC } from 'react';
import { getApiErrorMessage } from '@/app/helpers/api-error.helpers.ts';
import {
  formatRepresentativeDeleteBlockers,
  type RepresentativeDeleteTarget,
  representativeDeleteCopy,
} from '@/app/helpers/representative-delete.helpers.ts';
import toast from '@/app/toast.ts';
import { useDeleteRepresentativeMutation } from '@/slices/representative-api-slice';

interface DeleteRepresentativeDialogProps {
  representative: RepresentativeDeleteTarget | null;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onDeleted?: () => void;
}

const DeleteRepresentativeDialog: FC<DeleteRepresentativeDialogProps> = ({
  representative,
  isOpen,
  setIsOpen,
  onDeleted,
}) => {
  const [deleteRep, { isLoading }] = useDeleteRepresentativeMutation();
  const canDelete = Boolean(representative?.canDelete);
  const blockers = representative?.cannotDeleteReasons ?? [];
  const isBlockedView = !canDelete;

  const closeDialog = () => {
    setIsOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!representative) {
      return;
    }

    try {
      await deleteRep(representative.key).unwrap();
      toast.success(representativeDeleteCopy.deleteSuccess);
      closeDialog();
      onDeleted?.();
    } catch (error) {
      toast.error(getApiErrorMessage(error, representativeDeleteCopy.deleteError));
    }
  };

  if (!representative) {
    return null;
  }

  const displayName = `${representative.firstName} ${representative.lastName}`.trim();

  return (
    <Modal isOpen={isOpen} onClose={closeDialog} placement='center' backdrop='blur'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          {isBlockedView ? 'Cannot Delete Representative' : 'Delete Representative?'}
        </ModalHeader>
        <ModalBody className='flex flex-col gap-3'>
          {isBlockedView ? (
            <>
              <p className='text-sm text-gray-600'>
                {representativeDeleteCopy.blockedIntro(
                  representative.firstName,
                  representative.lastName,
                )}
              </p>
              {blockers.length > 0 ? (
                <ul className='list-disc space-y-1 pl-5 text-sm text-gray-700'>
                  {blockers.map(reason => (
                    <li key={reason}>{reason.charAt(0).toUpperCase() + reason.slice(1)}</li>
                  ))}
                </ul>
              ) : (
                <p className='text-sm text-gray-600'>
                  {representativeDeleteCopy.blockedReasonPrefix}{' '}
                  {formatRepresentativeDeleteBlockers(blockers) ||
                    'they are not eligible for deletion.'}
                </p>
              )}
              <p className='text-sm text-gray-500'>
                Remove these dependencies first, or contact MeetingMD support if you believe this is
                an error.
              </p>
            </>
          ) : (
            <>
              <p className='text-sm text-gray-600'>
                {representativeDeleteCopy.deleteConfirm(
                  representative.firstName,
                  representative.lastName,
                )}
              </p>
              <p className='text-sm font-medium text-danger-600'>
                You are about to permanently delete {displayName}.
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='flat' onPress={closeDialog}>
            {isBlockedView ? 'Close' : 'Cancel'}
          </Button>
          {!isBlockedView && (
            <Button
              color='danger'
              onPress={handleConfirmDelete}
              isLoading={isLoading}
              startContent={!isLoading ? <TrashIcon className='size-5' /> : undefined}
            >
              Delete Permanently
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteRepresentativeDialog;
